import {
  Box,
  TextField,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useAuth } from "../../../app/authHooks";
import { useNavigate, Link } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { UserScan } from "iconoir-react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setForgotUsername } from "../../../app/authSlice";


const initialValues = {
  username: "",
};

const forgotSchema = yup.object().shape({
  username: yup.string(),
});

const ForgotPasswordForm = () => {
  const theme = useTheme();
  const colors = theme.palette;
  const auth = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handleFormSubmit = async (values: any) => {
    setIsLoading(true);
    const result = await auth.forgotPassword(values.username);
    setIsLoading(false);
    if (result.success) {
      dispatch(setForgotUsername(values.username)); // Store username temporarily
      enqueueSnackbar(result.message, { variant: "success" });
      navigate("/auth/reset-password");
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  return (
    <Box sx={{ background: colors.primary.light, minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      <Box sx={{ marginBottom: 4 }}>
        <Box
          component="img"
          alt="ss&c-logo"
          src="../../../assets/images/ssc-hubwise-logo.svg"
          sx={{
            width: 250,
            height: "auto"
          }}
        />
      </Box>
      <Box
        sx={{
          p: 5,
          background: colors.grey[900],
          borderRadius: 4,
          boxShadow: "1px 3px 10px rgba(202, 202, 202, 0.83)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: colors.grey[400] }}>Admin Platform</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Forgot Password</Typography>
        </Box>

        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={forgotSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 5
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    label="Username"
                    size="small"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.username}
                    name="username"
                    error={!!touched.username && !!errors.username}
                    helperText={!!touched.username && errors.username}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ fontSize: 14 }}>
                          <UserScan />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="end" mt="20px">
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    endIcon={isLoading ? <CircularProgress size={20} /> : null}
                  >
                    {isLoading ? "Sending..." : "Send Reset Code"}
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", marginTop: 2, gap: 1 }}>
        <Typography variant="h5">By clicking continue, you agree to our</Typography>
        <Typography variant="h5"><Typography variant="h5" component={Link} to="#" sx={{
          color: colors.grey[400],
          ":hover": {
            color: colors.primary.main
          }
        }}>
          Terms of Service
        </Typography> and <Typography variant="h5" component={Link} to="#" sx={{
          color: colors.grey[400],
          ":hover": {
            color: colors.primary.main
          }
        }}>
            Privacy Policy
        </Typography></Typography>
      </Box>
    </Box>
  );
};

export default ForgotPasswordForm;
